import { units, effects, weights, rarities, unitDef, effectDef, enemyDef, EnemyDef, CombatType, types, countdowns, openings, spawnTimes, mechanics, budgets, dialogue, sideNames, achievementCollectibles } from './content';
export type Priority = 'Important' | 'Normal' | 'Ignore';
export type Status = 'unassigned' | 'wandering' | 'traveling' | 'ready' | 'active' | 'reserve' | 'camp-bound' | 'camping' | 'retreating' | 'KO';
export interface Unit {
    id: string;
    form: string;
    hp: number;
    x: number;
    y: number;
    status: Status;
    target: string | null;
    ax: number;
    ay: number;
    clock: number;
    attacks: number;
    arrival: number;
    koTime: number;
    shield: number;
    shieldUntil: number;
    shields: {
        family: string;
        amount: number;
        until: number;
    }[];
    lastHit: number;
    buffUntil: number;
    buff: number;
    seen: string[];
    flags: Record<string, number>;
}
export interface Objective {
    id: string;
    def: string;
    name: string;
    type: CombatType;
    group: 'opening' | 'mechanic' | 'boss' | 'side';
    hp: number;
    max: number;
    work: boolean;
    x: number;
    y: number;
    seats: number;
    reserves: number;
    priority: Priority;
    strategy: 'Rush' | 'Careful';
    spawn: number;
    done: boolean;
    engaged: boolean;
    clock: number;
    areaClock: number;
    cursor: number;
    fallback: number;
    targetSize: number;
    phase: number;
    flags: Record<string, number>;
    contributors: string[];
}
export interface ActiveEffect {
    id: string;
    card: string;
    x: number;
    y: number;
    remaining: number;
    delay: number;
}
export interface Telegraph {
    id: string;
    source: string;
    x: number;
    y: number;
    shape: string;
    remaining: number;
    damage: number;
    type: CombatType;
    angle: number;
    safeX?: number;
    safeY?: number;
}
export interface Draft {
    source: string;
    tier: number;
    cards: string[];
}
export interface Float {
    id: number;
    x: number;
    y: number;
    text: string;
    color: string;
    life: number;
}
export interface AttackVisual {
    id: string;
    type: string;
    fromX: number;
    fromY: number;
    x: number;
    y: number;
    life: number;
}
export interface RewardVisual {
    id: string;
    kind: 'objective' | 'quest';
    x: number;
    y: number;
    drafts: number;
    xp: number;
}
export interface Run {
    seed: number;
    rng: number;
    drawRng: number;
    commands: {
        kind: 'priority' | 'camp' | 'wake';
        id?: string;
        priority?: Priority;
    }[];
    route: number;
    hard: boolean;
    floor: number;
    time: number;
    elapsed: number;
    floorElapsed: number;
    carry: number;
    xp: number;
    tier: number;
    units: Unit[];
    objectives: Objective[];
    effects: ActiveEffect[];
    passives: string[];
    held: string[];
    rerolls: number;
    rewardVfx: RewardVisual[];
    attackVfx: AttackVisual[];
    vfx: { id: number; card: string; x: number; y: number; life: number }[];
    bonus: number;
    queue: Draft[];
    pending: string | null;
    targeting: string | null;
    anchor: string | null;
    replaceEffect: string | null;
    phase: 'playing' | 'transition' | 'victory' | 'defeat' | 'ending';
    menu: boolean;
    cleared: boolean;
    rewarded: string[];
    stage: number;
    mechanicStart: number;
    window: number;
    fog: number;
    telegraphs: Telegraph[];
    floats: Float[];
    serial: number;
    story: string[];
    storyIndex: number;
    storyClock: number;
    seenLines: string[];
    achievements: string[];
    collectibles: string[];
    memory: number;
    kos: number;
    recovered: number;
    floorTimes: number[];
    log: string[];
    history: {
        action: string;
        card: string;
        floor: number;
        tier: number;
    }[];
    lastLocal: string | null;
    ending: string | null;
    shotChoice: string | null;
    wallStart: number;
    draftSeconds: number;
    pauseSeconds: number;
}
export const distance = (a: {
    x: number;
    y: number;
}, b: {
    x: number;
    y: number;
}) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
export const multiplier = (a: string, b: string) => a === 'Neutral' || b === 'Neutral' || a === b ? 1 : ((a === 'Melee' && b === 'Range') || (a === 'Range' && b === 'Magic') || (a === 'Magic' && b === 'Melee')) ? 1.75 : .5;
// The first reinforcement arrives halfway through the four-enemy opening.
// Rejoin the main curve at tier 4 so later evolution and the total XP stay intact.
export const threshold = (tier: number) => tier === 2 ? 40 : tier === 3 ? 400 : 100 * tier * (tier - 1);
const random = (s: Run) => { s.rng = (Math.imul(1664525, s.rng) + 1013904223) >>> 0; return s.rng / 4294967296; };
const drawRandom = (s: Run) => { s.drawRng = (Math.imul(1664525, s.drawRng) + 1013904223) >>> 0; return s.drawRng / 4294967296; };
const alive = (u: Unit) => u.hp > 0 && !['KO', 'retreating'].includes(u.status);
const active = (s: Run, o: Objective) => s.units.filter(u => u.target === o.id && ['active', 'ready'].includes(u.status) && alive(u));
export const visibleObjectives = (s: Run) => s.objectives.filter(o => !o.done && o.spawn <= s.floorElapsed);
const uid = (s: Run, prefix: string) => `${prefix}-${++s.serial}`;
function note(s: Run, text: string) { s.log = [text, ...s.log].slice(0, 100); }
function float(s: Run, p: {
    x: number;
    y: number;
}, text: string, color = '#e6bd73') { s.floats.push({ id: ++s.serial, x: p.x, y: p.y, text, color, life: 1.4 }); }
function achieve(s: Run, id: string) { if (!s.achievements.includes(id)) {
    s.achievements.push(id);
    const collectible = achievementCollectibles[id];
    if (collectible && !s.collectibles.includes(collectible))
        s.collectibles.push(collectible);
    note(s, 'Achievement unlocked');
} }
function story(s: Run, trigger: string) { const line = dialogue.find(d => d.route === s.route && d.floor === s.floor && d.trigger === trigger); if (line && !s.seenLines.includes(line.id)) {
    if (trigger === 'loaded' && s.floor !== 1)
        s.story.unshift(line.id);
    else
        s.story.push(line.id);
    s.seenLines.push(line.id);
} }
function campPosition(s: Run, u: Unit) {
    const index = Math.max(0, s.units.indexOf(u));
    // A golden-angle scatter gives each ally a stable spot, without grid rows
    // or consuming combat randomness. New recruits never move existing spots.
    const angle = index * Math.PI * (3 - Math.sqrt(5));
    const radius = Math.sqrt((index + 1) / (index + 12));
    return { x: 50 + Math.cos(angle) * 23 * radius, y: 39.3 + Math.sin(angle) * 3 * radius };
}
export function makeUnit(s: Run, form: string): Unit { const d = unitDef(form); return { id: uid(s, 'u'), form, hp: d.hp, x: 47 + random(s) * 6, y: 38 + random(s) * 2, status: 'unassigned', target: null, ax: 50, ay: 39.5, clock: 0, attacks: 0, arrival: 0, koTime: 0, shield: 0, shieldUntil: 0, shields: [], lastHit: -99, buffUntil: 0, buff: 0, seen: [], flags: {} }; }
function makeObjective(s: Run, def: string, group: Objective['group'], index: number, count: number, spawn = 0): Objective {
    const e = enemyDef(def);
    const m = mechanics[s.floor - 1];
    const isWork = !e;
    const names = s.floor === 4 ? ['Blue sigil', 'Green sigil', 'Red sigil'] : s.floor === 11 ? ['Line anchor', 'Cone anchor', 'Ring anchor', 'Pulse anchor'] : s.floor === 12 ? (s.route === 1 ? ['Cone anchor', 'Line anchor', 'Ring anchor', 'Pulse anchor'] : ['Lane anchor', 'Ring anchor', 'Cone anchor', 'Pulse anchor']) : s.floor === 9 ? ['West beacon', 'East beacon'] : [];
    return { id: uid(s, 'o'), def, name: e?.name || names[index] || `${m[0]} ${index + 1}`, type: e?.type || (s.floor === 10 ? types[index % 3] : 'Neutral'), group, hp: e?.hp || m[2], max: e?.hp || m[2], work: isWork && s.floor !== 10, x: count === 1 ? 50 : (32 - Math.min(12, s.floor - 1)) + ((36 + Math.min(24, (s.floor - 1) * 2)) / (count - 1)) * index, y: group === 'boss' ? 14 : group === 'mechanic' ? 25 : 12 + (index % 2) * 7, seats: e?.seats || m[3], reserves: e?.reserves ?? (s.floor === 13 ? 0 : 1), priority: 'Normal', strategy: group === 'boss' ? 'Careful' : 'Rush', spawn, done: false, engaged: false, clock: 0, areaClock: 0, cursor: 0, fallback: 0, targetSize: e?.seats || m[3], phase: 0, flags: { index }, contributors: [] };
}
function loadFloor(s: Run) {
    s.floorElapsed = 0;
    s.stage = 0;
    s.cleared = false;
    s.mechanicStart = 0;
    s.window = 0;
    s.fog = 0;
    s.telegraphs = [];
    s.vfx = [];
    s.attackVfx = [];
    s.rewardVfx = [];
    s.objectives = [];
    const carried = s.story.slice(s.storyIndex).filter(id => id.endsWith('-end')).slice(-1);
    s.story = carried;
    s.storyIndex = 0;
    s.storyClock = 0;
    const rows = [...openings[s.floor - 1], ...openings[s.floor - 1]];
    const times = [...spawnTimes[s.floor - 1], ...spawnTimes[s.floor - 1].map(t => t + 3)];
    if (s.hard && s.floor >= 5) {
        rows.push(rows[0]);
        times.push(4);
    }
    rows.forEach((id, i) => s.objectives.push(makeObjective(s, id, 'opening', i, rows.length, times[i] * (s.hard ? .8 : 1) + (s.floor === 1 ? 10 : 0))));
    if ([1, 2, 3].includes(s.floor)) {
        const o = makeObjective(s, 'N', 'side', 0, 1, s.floor === 1 ? 10 : 0);
        Object.assign(o, { name: s.floor === 2 ? 'Optional rescue cage' : 'Optional ore cache', x: 88, y: 30, max: s.floor === 2 ? 150 : 180, hp: s.floor === 2 ? 150 : 180, seats: 3 });
        s.objectives.push(o);
    }
    if (s.floor === 1) { story(s, 'arrival'); story(s, 'prepare'); }
    story(s, 'loaded');
    note(s, `Floor ${s.floor}: expedition continues`);
}
export function newRun(seed = Date.now() >>> 0, route = 1, hard = false): Run {
    const s: Run = { seed, rng: seed, drawRng: (seed ^ 0x9e3779b9) >>> 0, commands: [], route, hard, floor: 1, time: hard ? 110 : 120, elapsed: 0, floorElapsed: 0, carry: 0, xp: 0, tier: 1, units: [], objectives: [], effects: [], passives: [], vfx: [], attackVfx: [], rewardVfx: [], held: ['FX-T01'], rerolls: 1, bonus: 0, queue: [], pending: null, targeting: null, anchor: null, replaceEffect: null, phase: 'playing', menu: false, cleared: false, rewarded: [], stage: 0, mechanicStart: 0, window: 0, fog: 0, telegraphs: [], floats: [], serial: 0, story: [], storyIndex: 0, storyClock: 0, seenLines: [], achievements: [], collectibles: [], memory: 0, kos: 0, recovered: 0, floorTimes: [], log: [], history: [], lastLocal: null, ending: null, shotChoice: null, wallStart: Date.now(), draftSeconds: 0, pauseSeconds: 0 };
    s.units = ['U01A', 'U07A', 'U13A'].map(f => makeUnit(s, f));
    loadFloor(s);
    return s;
}
export const paused = (s: Run) => s.phase !== 'playing' || s.menu || s.queue.length > 0 || !!s.pending || !!s.targeting || !!s.replaceEffect || !!s.shotChoice;
// Terminal screens are outside expedition timing. Draft menus and pause menus are separate.
export function recordWallTime(s: Run, seconds: number) {
    if (['victory', 'defeat', 'ending'].includes(s.phase)) return;
    if (s.queue.length && !s.menu) s.draftSeconds += seconds;
    else if (paused(s)) s.pauseSeconds += seconds;
}
export function advanceStory(s: Run) { s.storyIndex = Math.min(s.story.length, s.storyIndex + 1); s.storyClock = 0; }
function scene(s: Run, trigger: string) { dialogue.filter(d => d.route === s.route && d.trigger === trigger).forEach(d => { if (!s.seenLines.includes(d.id)) {
    s.story.push(d.id);
    s.seenLines.push(d.id);
} }); }
export function chooseEnding(s: Run, choice: 'Bind the Crown' | 'Break the Crown') { if (s.phase !== 'ending')
    return; s.ending = choice; s.phase = 'victory'; s.story = []; s.storyIndex = 0; scene(s, choice === 'Bind the Crown' ? 'bind' : 'break'); }
function applyPriority(s: Run, id: string, p: Priority) { const o = s.objectives.find(o => o.id === id); if (!o || o.done)
    return; const oldPriority = o.priority; o.priority = p; if (s.floor === 7 && s.route === 3 && oldPriority !== p) {
    s.objectives.filter(a => a.def === 'B07' && a.flags.copiedPriority === ['Important', 'Normal', 'Ignore'].indexOf(oldPriority)).forEach(a => { s.telegraphs.filter(t => t.source === a.id).forEach(t => { t.x = o.x; t.y = o.y; }); });
} achieve(s, 'ACH_FIRST_COMMAND'); note(s, `${o.name}: ${p}`); s.units.filter(u => u.target === id).forEach(u => { if (p === 'Ignore') {
    u.target = null;
    u.status = 'unassigned';
} }); if (p === 'Important')
    s.units.filter(u => u.status === 'camping' && alive(u)).forEach(u => u.status = 'unassigned'); }
function applyCamp(s: Run, id: string) { const party = s.units.filter(u => u.target === id && alive(u)); applyPriority(s, id, 'Ignore'); party.forEach(u => { u.target = null; u.status = 'camp-bound'; const camp = campPosition(s, u); u.ax = camp.x; u.ay = camp.y; }); }
function applyWake(s: Run, id?: string) { s.units.filter(u => (!id || u.id === id) && u.status === 'camping' && alive(u)).forEach(u => u.status = 'unassigned'); }
export function setPriority(s: Run, id: string, p: Priority) { s.commands.push({ kind: 'priority', id, priority: p }); }
export function campParty(s: Run, id: string) { s.commands.push({ kind: 'camp', id }); }
export function wake(s: Run, id?: string) { s.commands.push({ kind: 'wake', id }); }
export function evolve(s: Run) { s.units.forEach(u => { const d = unitDef(u.form); const stage = s.tier >= 16 ? 2 : s.tier >= 8 ? 1 : 0; if (d.stage < stage) {
    const next = unitDef(d.line + 'ABC'[stage]);
    u.hp = u.hp / d.hp * next.hp;
    u.form = next.id;
    u.attacks = 0;
    u.flags = {};
    u.buff = 0;
    u.buffUntil = 0;
    u.shield = 0;
    u.shields = [];
    float(s, u, 'EVOLVED', '#bba3ff');
    achieve(s, 'ACH_FIRST_EVOLUTION');
} }); }
function reward(s: Run, group: string, share: number) {
    const id = `${s.floor}-${group}`;
    if (s.rewarded.includes(id)) return;
    s.rewarded.push(id);
    const before = s.xp;
    const amount = budgets[s.floor - 1] * share * 2;
    s.xp = Math.min(threshold(20), s.xp + amount);
    // One draft halfway through a tier, one at its end: twice the recruitment
    // cadence without accelerating the authored evolution and rarity curve.
    for (let tier = 1; tier < 20; tier++) {
        const midpoint = (threshold(tier) + threshold(tier + 1)) / 2;
        if (before < midpoint - .0001 && s.xp >= midpoint - .0001)
            s.queue.push({ source: `Fame ${tier} · Reinforcements`, tier, cards: [] });
        if (before < threshold(tier + 1) - .0001 && s.xp >= threshold(tier + 1) - .0001) {
            s.tier = tier + 1;
            s.queue.push({ source: `Fame tier ${s.tier}`, tier: s.tier, cards: [] });
        }
    }
    evolve(s);
}
function collect(s: Run) { const id = `COL_SIDE_L${String(s.floor).padStart(2, '0')}_R${s.route}`; if (!s.collectibles.includes(id)) {
    s.collectibles.push(id);
    s.memory += 10;
    note(s, `Recovered: ${sideNames[s.floor - 1]}`);
} }
export function offer(s: Run, d: Draft) {
    const selected: string[] = [];
    const lines: string[] = [];
    const eligibleUnits = units.filter(u => d.tier >= Math.max(u.unlock, [1, 4, 12][u.stage]) && d.tier < [8, 16, 21][u.stage]);
    const eligibleEffects = effects.filter(e => e.unlock <= d.tier && !s.passives.includes(e.id) && (!(e.id === 'FX-T02' || e.id === 'FX-T03') || (s.bonus < 2 && !d.source.startsWith('Bonus'))) && (e.id !== 'FX-T04' || !!s.lastLocal) && (e.id !== 'FX-I05' || s.units.some(u => !alive(u))) && (e.id !== 'FX-I06' || s.units.some(u => u.flags.slow > s.elapsed)) && (!['FX-I03', 'FX-I07', 'FX-I08'].includes(e.id) || visibleObjectives(s).some(o => !o.work)) && (!['FX-I01', 'FX-I02', 'FX-I04'].includes(e.id) || s.units.some(u => alive(u) && u.hp < unitDef(u.form).hp)));
    // Reserve the Miner lineage so the other recruit cannot consume its guarantee.
    const miner = unitDef('U19' + (d.tier >= 16 ? 'C' : d.tier >= 8 ? 'B' : 'A'));
    const recruitPool = eligibleUnits.filter(u => u.line !== miner.line);
    const roll = drawRandom(s) * 100;
    let sum = 0;
    let rarity = 0;
    for (let i = 0; i < 5; i++) {
        sum += weights[d.tier - 1][i];
        if (roll < sum) { rarity = i; break; }
    }
    const order = [rarity, ...Array.from({ length: rarity }, (_, i) => rarity - i - 1), ...Array.from({ length: 4 - rarity }, (_, i) => rarity + i + 1)];
    const recruit = order.map(r => recruitPool.filter(u => u.rarity === rarities[r])).find(pool => pool.length)!;
    const choose = (pool: { id: string }[]) => {
        const card = pool[Math.floor(drawRandom(s) * pool.length)];
        selected.push(card.id);
        if (card.id.startsWith('U')) lines.push(unitDef(card.id).line);
    };
    choose(recruit);
    choose(eligibleEffects);
    selected.push(miner.id);
    lines.push(miner.line);
    // Unrestricted slots are uniform over all eligible remaining cards.
    for (let i = 0; i < 2; i++) {
        choose([
            ...eligibleUnits.filter(u => !lines.includes(u.line)),
            ...eligibleEffects.filter(e => !selected.includes(e.id))
        ]);
    }
    return selected;
}
export function ensureDraft(s: Run) { if (s.queue.length && !s.queue[0].cards.length)
    s.queue[0].cards = offer(s, s.queue[0]); }
export function reroll(s: Run) { if (!s.queue.length || !s.held.includes('FX-T01') || s.pending)
    return; s.held.splice(s.held.indexOf('FX-T01'), 1); s.rerolls = s.held.filter(id => id === 'FX-T01').length; s.history.push({ action: 'reroll', card: s.queue[0].cards.join(','), floor: s.floor, tier: s.queue[0].tier }); s.queue[0].cards = offer(s, s.queue[0]); }
function recordCard(s: Run, action: string, card: string) { s.history.push({ action, card, floor: s.floor, tier: s.tier }); }
export function pick(s: Run, id: string) {
    if (!s.queue[0]?.cards.includes(id) || s.pending)
        return;
    recordCard(s, 'draft', id);
    if (id.startsWith('U')) {
        s.units.push(makeUnit(s, id));
        evolve(s);
        if (s.units.length === 18)
            achieve(s, 'ACH_FULL_ROSTER');
    }
    else {
        const e = effectDef(id);
        if (e.kind.startsWith('Kept')) {
            if (s.held.length >= 6) {
                s.pending = id;
                return;
            }
            s.held.push(id);
            s.rerolls = s.held.filter(card => card === 'FX-T01').length;
        }
        else if (e.kind === 'Run passive') {
            s.vfx.push({ id: ++s.serial, card: id, x: 50, y: 39.5, life: 2.2 });
            if (!s.passives.includes(id))
                s.passives.push(id);
        }
        else if (e.kind === 'Token') {
            if (id === 'FX-T04') {
                if (s.lastLocal) {
                    if (s.held.length >= 6) {
                        s.pending = s.lastLocal;
                        return;
                    }
                    s.held.push(s.lastLocal);
                }
            }
            else if (s.bonus < 2) {
                s.bonus++;
                s.queue.push({ source: 'Bonus draw', tier: s.tier, cards: [] });
            }
        }
        else
            instant(s, id);
    }
    s.queue.shift();
    ensureDraft(s);
    postDraft(s);
}
export function replacePending(s: Run, index: number | null) { if (!s.pending)
    return; const id = s.pending; if (index === null) {
    s.memory += 10;
    recordCard(s, 'decline', id);
}
else if (id.startsWith('U')) {
    if (!s.units[index])
        return;
    recordCard(s, 'replace', s.units[index].form);
    s.units[index] = makeUnit(s, id);
}
else {
    if (!s.held[index])
        return;
    recordCard(s, 'replace', s.held[index]);
    s.held[index] = id;
} s.rerolls = s.held.filter(card => card === 'FX-T01').length; s.pending = null; s.queue.shift(); ensureDraft(s); postDraft(s); }
function postDraft(s: Run) { if (!s.queue.length && s.cleared && s.phase === 'playing') {
    story(s, 'clear');
    story(s, 'end');
    s.phase = 'transition';
} }
function instant(s: Run, id: string) {
    const targets = ['FX-I03', 'FX-I07', 'FX-I08'].includes(id) ? visibleObjectives(s).filter(o => !o.work) : s.units;
    targets.forEach(p => s.vfx.push({ id: ++s.serial, card: id, x: p.x, y: p.y, life: 1.8 }));
    const heals: Record<string, number> = { 'FX-I01': .08, 'FX-I02': .06, 'FX-I04': .12 };
    if (heals[id])
        s.units.filter(alive).forEach(u => heal(s, u, unitDef(u.form).hp * heals[id]));
    if (id === 'FX-I02')
        s.units.filter(u => u.status === 'traveling').forEach(u => move(u, 4));
    if (id === 'FX-I05')
        s.units.filter(u => !alive(u)).forEach(u => { u.hp = unitDef(u.form).hp * .2; u.status = 'unassigned'; u.x = 50; u.y = 39.5; });
    if (id === 'FX-I06')
        s.units.forEach(u => u.flags.slow = 0);
    const attacks: Record<string, [
        number,
        CombatType
    ][]> = { 'FX-I03': [[24, 'Magic']], 'FX-I07': [[36, 'Range']], 'FX-I08': [[20, 'Melee'], [20, 'Range'], [20, 'Magic']] };
    if (attacks[id])
        visibleObjectives(s).filter(o => !o.work).forEach(o => attacks[id].forEach(([d, t]) => hitEnemy(s, o, d, t, 0, false)));
}
export function targetCard(s: Run, id: string) { if (s.phase !== 'playing' || s.menu || (s.queue.length > 0 && id !== 'FX-T01') || !s.held.includes(id) || s.targeting)
    return; if (id === 'FX-T01') { reroll(s); return; } s.targeting = id; s.anchor = effectDef(id).kind === 'Kept global' ? 'camp' : visibleObjectives(s)[0]?.id || s.units[0]?.id || 'camp'; }
export function cancelTarget(s: Run) { s.targeting = null; s.anchor = null; s.replaceEffect = null; }
export function anchorPoint(s: Run, id: string | null): {
    x: number;
    y: number;
} | undefined { return id === 'camp' ? { x: 50, y: 39.5 } : visibleObjectives(s).find(o => o.id === id) || s.units.find(u => u.id === id); }
export function effectRadius(id: string) { return Number(effectDef(id).scope.match(/Radius (\d+)/)?.[1] || 0); }
export function cast(s: Run, endEffect?: string) {
    const id = s.targeting;
    if (!id)
        return;
    const e = effectDef(id);
    const p = anchorPoint(s, s.anchor);
    if (e.kind === 'Kept local' && !p) {
        cancelTarget(s);
        return;
    }
    const duration = Number(e.timing.match(/\d+/)?.[0] || 0);
    const occupied = s.effects.filter(f => f.card !== id && effectDef(f.card).timing !== 'Immediate');
    if ((duration || e.timing === 'Floor-long') && occupied.length >= 4) {
        if (!endEffect) {
            s.replaceEffect = id;
            return;
        }
        s.effects = s.effects.filter(f => f.id !== endEffect);
    }
    const existing = s.effects.find(f => f.card === id);
    if (existing) {
        existing.remaining = Math.max(existing.remaining, duration || 9999);
        existing.delay = .5;
        existing.x = p?.x || 50;
        existing.y = p?.y || 39.5;
    }
    else
        s.effects.push({ id: uid(s, 'fx'), card: id, x: p?.x || 50, y: p?.y || 39.5, remaining: duration || (e.timing === 'Floor-long' ? 9999 : .1), delay: .5 });
    const visualTargets = e.kind === 'Kept global' ? s.units.filter(alive) : [p || { x: 50, y: 39.5 }];
    visualTargets.forEach(target => s.vfx.push({ id: ++s.serial, card: id, x: target.x, y: target.y, life: 2.2 }));
    s.held.splice(s.held.indexOf(id), 1);
    recordCard(s, 'use', id);
    cancelTarget(s);
}
function inEffect(s: Run, u: {
    x: number;
    y: number;
}, ids: string[]) { return s.effects.filter(f => f.delay <= 0 && ids.includes(f.card) && (effectDef(f.card).kind !== 'Kept local' || distance(u, f) <= effectRadius(f.card))); }
function bonus(s: Run, u: Unit, kind: string) { const values: Record<string, Record<string, number>> = { attack: { 'FX-L04': .2, 'FX-G03': .15, 'FX-G06': .25, 'FX-G09': .35, 'FX-G10': .4 }, work: { 'FX-L02': .2, 'FX-G02': .15, 'FX-G07': .3, 'FX-G10': .2 }, mitigation: { 'FX-L01': .2, 'FX-L07': .3, 'FX-G01': .15, 'FX-G05': .25, 'FX-G08': .3 }, healing: { 'FX-G04': .25 } }; return inEffect(s, u, Object.keys(values[kind])).reduce((sum, f) => sum + values[kind][f.card], 0); }
function heal(s: Run, u: Unit, amount: number) { const max = unitDef(u.form).hp; const actual = Math.min(max - u.hp, amount * (1 + Math.min(.5, bonus(s, u, 'healing')))); u.hp += actual; if (actual >= 1)
    float(s, u, `+${Math.round(actual)}`, '#8adbb8'); }
function shield(s: Run, u: Unit, value: number, duration = 8, family = 'generic') {
    const existing = u.shields.find(f => f.family === family);
    const other = u.shields.filter(f => f.family !== family).reduce((n, f) => n + f.amount, 0);
    const amount = Math.min(unitDef(u.form).hp * .25 - other, Math.max(existing?.amount || 0, value));
    if (existing) {
        existing.amount = amount;
        existing.until = Math.max(existing.until, s.elapsed + duration);
    }
    else
        u.shields.push({ family, amount, until: s.elapsed + duration });
    u.shield = u.shields.reduce((n, f) => n + f.amount, 0);
    u.shieldUntil = Math.max(...u.shields.map(f => f.until));
}
function damage(s: Run, u: Unit, amount: number, type: CombatType, ordinary = true, bypass = false) {
    if (!alive(u) || (u.status === 'camping' || (u.status === 'reserve' && !bypass)))
        return;
    const d = unitDef(u.form);
    let reduction = ordinary ? bonus(s, u, 'mitigation') : 0;
    if (ordinary && d.id === 'U02A' && s.elapsed - (u.flags.arrivedAt || s.elapsed) >= 2)
        reduction += .15;
    if (d.id === 'U05C' && u.flags.lastStand > s.elapsed)
        reduction += .2;
    if (ordinary) {
        if (d.id === 'U10A' && u.clock >= 5 && u.clock % 5 < 1 && u.flags.dodged !== Math.floor(u.clock / 5)) {
            u.flags.dodged = Math.floor(u.clock / 5);
            return;
        }
        const allies = s.units.filter(a => a.target === u.target && a.status === 'active');
        if (allies.some(a => a.form === 'U02C' && distance(a, u) <= 3 && a.id !== u.id))
            reduction += .12;
        const oracle = allies.find(a => a.form === 'U18A');
        if (oracle && !u.seen.includes(`oracle-${u.target}`)) {
            shield(s, u, 12, 8, 'oracle');
            u.seen.push(`oracle-${u.target}`);
        }
        const warden = allies.find(a => a.form === 'U06B' && s.elapsed - (a.flags.challenge || -10) >= 6);
        if (warden && warden.id !== u.id) {
            warden.flags.challenge = s.elapsed;
            damage(s, warden, amount * .8, type, true, bypass);
            return;
        }
    }
    let hit = bypass ? amount : amount * multiplier(type, d.type) * (1 - clamp(reduction, 0, .5));
    if (!bypass) {
        for (const layer of u.shields) {
            const absorb = Math.min(layer.amount, hit);
            layer.amount -= absorb;
            hit -= absorb;
        }
        u.shields = u.shields.filter(f => f.amount > 0);
        u.shield = u.shields.reduce((n, f) => n + f.amount, 0);
    }
    if (u.hp - hit <= 0) {
        const phoenix = inEffect(s, u, ['FX-L09'])[0];
        const mortal = ordinary && s.passives.includes('FX-P05') && !u.flags.mortal;
        if ((phoenix && !u.seen.includes(phoenix.id)) || mortal) {
            hit = u.hp - 1;
            if (phoenix)
                u.seen.push(phoenix.id);
            if (mortal)
                u.flags.mortal = 1;
        }
    }
    u.hp = Math.max(0, u.hp - hit);
    if (hit >= .5)
        float(s, u, `−${Math.round(hit)}`, '#f3958e');
    u.lastHit = s.elapsed;
    if (hit > 0)
        u.flags.lastDamage = s.elapsed;
    if (ordinary) {
        if (d.id === 'U01B') {
            u.buff = .2;
            u.buffUntil = s.elapsed + 3;
        }
        if (d.id === 'U03B' && s.elapsed - (u.flags.riposte || -5) >= 4) {
            u.flags.riposte = s.elapsed;
            u.flags.counter = s.elapsed + 2;
        }
        if (d.id === 'U05B') {
            u.buff = Math.min(.15, u.buff + .05);
            u.buffUntil = s.elapsed + 4;
        }
        const allies = s.units.filter(a => a.target === u.target && a.status === 'active');
        allies.filter(a => a.form === 'U15B' && s.elapsed - (a.flags.ice || -6) >= 5).slice(0, 1).forEach(a => { shield(s, u, 10, 8, 'ice'); a.flags.ice = s.elapsed; });
        if (d.id === 'U02B' && s.elapsed - (u.flags.interpose || -7) >= 6) {
            const low = allies.sort((a, b) => a.hp / unitDef(a.form).hp - b.hp / unitDef(b.form).hp)[0];
            if (low)
                shield(s, low, 15, 8, 'bulwark');
            u.flags.interpose = s.elapsed;
        }
    }
    if (u.hp === 0) {
        u.status = 'retreating';
        u.target = null;
        u.koTime = 0;
        const dist = distance(u, { x: 50, y: 39.5 });
        u.ax = u.x + (50 - u.x) * Math.min(1, 4 / (dist || 1));
        u.ay = u.y + (39.5 - u.y) * Math.min(1, 4 / (dist || 1));
        s.kos++;
        note(s, `${d.name} is retreating to recover`);
        s.units.filter(a => a.form === 'U06C' && a.status === 'active' && s.elapsed - (a.flags.hold || -11) >= 10).forEach(a => { shield(s, a, 30, 8, 'warden'); a.buff = .25; a.buffUntil = s.elapsed + 5; a.flags.hold = s.elapsed; });
    }
}
function move(u: Unit, amount: number) { const dx = u.ax - u.x, dy = u.ay - u.y, dist = Math.hypot(dx, dy); if (dist <= amount) {
    u.x = u.ax;
    u.y = u.ay;
    return true;
} u.x += dx / dist * amount; u.y += dy / dist * amount; return false; }
function assign(s: Run) {
    const objectives = visibleObjectives(s).filter(o => o.priority !== 'Ignore' && !o.flags.loaded).sort((a, b) => (a.priority === 'Important' ? 0 : 1) - (b.priority === 'Important' ? 0 : 1));
    s.units.forEach(u => { if (u.target && !objectives.some(o => o.id === u.target)) {
        u.target = null;
        if (alive(u))
            u.status = 'unassigned';
    } });
    for (const o of objectives) {
        let party = s.units.filter(u => u.target === o.id && ['traveling', 'ready', 'active'].includes(u.status) && alive(u));
        if (!o.work && !party.some(u => unitDef(u.form).attack > 0)) {
            party.filter(u => unitDef(u.form).heal > 0).forEach(u => { u.target = null; u.status = 'unassigned'; });
            party = party.filter(u => unitDef(u.form).attack > 0);
        }
        while (party.length < o.seats) {
            const candidates = s.units.filter(u => alive(u) && (['unassigned', 'wandering', 'reserve'].includes(u.status) || (o.priority === 'Important' && u.target && s.objectives.find(t => t.id === u.target)?.priority === 'Normal'))).filter(u => o.work || unitDef(u.form).attack > 0 || (unitDef(u.form).heal > 0 && party.some(a => unitDef(a.form).attack > 0 && a.hp < unitDef(a.form).hp) && !party.some(a => unitDef(a.form).heal > 0)));
            candidates.sort((a, b) => { const da = unitDef(a.form), db = unitDef(b.form); return (o.work ? db.work - da.work : multiplier(db.type, o.type) - multiplier(da.type, o.type)) || (o.work ? distance(a, o) - distance(b, o) : b.hp / db.hp - a.hp / da.hp) || distance(a, o) - distance(b, o) || a.id.localeCompare(b.id); });
            const nearby = candidates.filter(u => u.status === 'reserve' && u.target === o.id).sort((a, b) => distance(a, o) - distance(b, o) || a.id.localeCompare(b.id));
            const u = nearby[0] || candidates[0];
            if (!u)
                break;
            const d = unitDef(u.form);
            const radius = o.work || d.type === 'Melee' || d.type === 'Neutral' ? 1.8 : d.type === 'Range' ? 11 : 7;
            const angle = Math.PI * .18 + party.length * 2.399;
            u.ax = clamp(o.x + Math.cos(angle) * radius, .5, 99.5);
            u.ay = clamp(o.y + Math.sin(angle) * radius, .5, 42.35);
            u.target = o.id;
            u.status = 'traveling';
            u.arrival = 0;
            u.clock = 0;
            party.push(u);
        }
    }
    for (const o of objectives) {
        const current = s.units.filter(u => u.target === o.id && u.status === 'reserve');
        const idle = s.units.filter(u => alive(u) && ['unassigned', 'wandering'].includes(u.status) && (o.work || unitDef(u.form).attack > 0)).sort((a, b) => distance(a, o) - distance(b, o) || a.id.localeCompare(b.id));
        while (current.length < o.reserves && idle.length) {
            const u = idle.shift()!;
            u.target = o.id;
            u.status = 'reserve';
            u.ax = o.x + 3 + current.length;
            u.ay = o.y + 4;
            current.push(u);
        }
    }
}
function delayHit(s: Run, o: Objective, delay: number) { if (s.elapsed - (o.flags.delayed || -5) < 4)
    return; o.clock = Math.max(0, o.clock - delay); o.flags.delayed = s.elapsed; }
function hitEnemy(s: Run, o: Objective, amount: number, type: string, attackBonus = 0, allowBonuses = true) {
    let reduction = 0;
    if (o.def === 'E02' && visibleObjectives(s).some(e => e.id !== o.id && e.def === 'E02'))
        reduction += .2;
    if (o.def.startsWith('E') && visibleObjectives(s).some(e => e.id !== o.id && e.def === 'E07' && distance(e, o) <= 20))
        reduction += .15;
    if (o.flags.cracked > s.elapsed)
        reduction -= .1;
    let m = multiplier(type, o.type);
    if (s.floor === 13 && m > 1 && visibleObjectives(s).some(e => e.group === 'mechanic' && types[e.flags.index] === type))
        m = 1;
    let output = amount * m * (1 + clamp((allowBonuses ? attackBonus + ['archer', 'scout', 'hex'].reduce((n, k) => n + (o.flags[k + 'Until'] > s.elapsed ? o.flags[k + 'Value'] : 0), 0) + (type === 'Range' && o.flags.hawkUntil > s.elapsed ? .08 : 0) + (inEffect(s, o, ['FX-L08']).length ? .2 : 0) : 0), 0, .6)) * (1 - clamp(reduction, 0, .5));
    if (o.flags.retreatUntil > s.elapsed)
        return;
    if (o.flags.pursuitUntil > s.elapsed)
        output *= 1.2;
    if (o.def === 'B06' && s.window <= 0)
        output *= .25;
    if (o.def === 'B09' && (s.stage < 2 || s.window <= 0))
        output *= .25;
    if (o.def === 'B07' && s.route === 2) {
        const twin = visibleObjectives(s).find(a => a.def === 'B07' && a.id !== o.id);
        o.flags.stompCharge = Math.max(0, (o.flags.stompCharge || 0) - output * .5);
        if (twin) {
            twin.flags.stompCharge = (twin.flags.stompCharge || 0) + output * .5;
            if (twin.flags.stompCharge >= 180) {
                twin.flags.stompCharge = 0;
                queueArea(s, twin, 'pulse', 20);
            }
        }
    }
    o.hp -= output;
    if (output >= 1)
        float(s, o, `${Math.round(output)}`, type === 'Magic' ? '#c5aaff' : type === 'Range' ? '#8fd6b3' : '#e4bf82');
}
function attack(s: Run, u: Unit, o: Objective) {
    const d = unitDef(u.form);
    const allies = active(s, o).filter(a => a.status === 'active');
    if (o.work)
        u.flags.workTicks = (u.flags.workTicks || 0) + 1;
    else if (d.heal > 0)
        u.flags.healTicks = (u.flags.healTicks || 0) + 1;
    else
        u.attacks++;
    if (!o.contributors.includes(u.id))
        o.contributors.push(u.id);
    if (o.work) {
        let work = d.work;
        if (s.floor === 4) {
            const rotation = Math.floor((s.elapsed - s.mechanicStart) / 12) % 3;
            work *= rotation === o.flags.index ? 1 : .25;
            if (s.route === 2 && rotation === o.flags.index && ((s.elapsed - s.mechanicStart) % 12) < 9)
                work = Math.min(2, work);
            if (s.route === 3 && d.type === types[o.flags.index])
                work *= 2;
        }
        if (s.floor === 3 && s.route === 2 && Math.floor((s.elapsed - s.mechanicStart) / 8) % 2 === 1)
            work *= .5;
        if (d.id === 'U19A' && !u.seen.includes(o.id)) {
            work += 15;
            u.seen.push(o.id);
        }
        if (d.id === 'U19B' && u.flags.workTicks % 5 === 0)
            work += 10;
        if (d.id === 'U19C' && u.flags.workTicks % 10 === 0)
            work += 25;
        o.hp -= work * (1 + Math.min(.6, bonus(s, u, 'work')));
        o.flags.lastWorked = s.elapsed;
        if (s.floor === 1 && s.route === 3) {
            o.flags.pulse = (o.flags.pulse || 0) + work;
            if (o.flags.pulse >= 60) {
                o.flags.pulse -= 60;
                s.objectives.filter(t => t.group === 'mechanic' && !t.done).forEach(t => t.hp -= 15);
            }
        }
        return;
    }
    if (d.heal > 0) {
        const low = allies.filter(alive).sort((a, b) => a.hp / unitDef(a.form).hp - b.hp / unitDef(b.form).hp)[0];
        if (low) {
            heal(s, low, d.heal * (d.id === 'U20B' && low.hp / unitDef(low.form).hp < .4 ? 1.3 : 1));
            if (d.id === 'U20A' && !u.seen.includes(low.id + o.id)) {
                shield(s, low, 6, 8, 'medic');
                u.seen.push(low.id + o.id);
            }
            if (d.id === 'U20C' && u.flags.healTicks % 10 === 0)
                allies.forEach(a => heal(s, a, 4));
        }
        return;
    }
    if (d.attack > 0) s.attackVfx.push({ id: `${u.id}-${u.attacks}`, type: d.type, fromX: u.x, fromY: u.y, x: o.x, y: o.y, life: .7 });
    let b = bonus(s, u, 'attack') + (u.buffUntil > s.elapsed ? u.buff : 0);
    let extra = 0;
    const first = !u.seen.includes(o.id);
    if (first) {
        u.seen.push(o.id);
        u.flags.entry = s.elapsed;
        u.flags.targetAttacks = 0;
        if (s.passives.includes('FX-P03'))
            extra += 30;
        if (d.id === 'U07C' && s.elapsed - (u.flags.markCooldown || -9) >= 8) {
            o.flags.archerUntil = s.elapsed + 5;
            o.flags.archerValue = .1;
            u.flags.markCooldown = s.elapsed;
        }
    }
    u.flags.targetAttacks = (u.flags.targetAttacks || 0) + 1;
    if (d.id === 'U01A' && u.attacks % 4 === 0)
        extra += 8;
    if (d.id === 'U03A' && s.elapsed - u.flags.entry < 3)
        b += .25;
    if (d.id === 'U03B' && u.flags.counter > s.elapsed) {
        extra += 12;
        u.flags.counter = 0;
    }
    if (d.id === 'U03C' && u.flags.targetAttacks % 3 === 0)
        shield(s, u, 12, 2, 'tempo');
    if (d.id === 'U04B' && u.attacks % 5 === 0)
        delayHit(s, o, .4);
    if (d.id === 'U04C' && allies.some(a => a.id !== u.id && unitDef(a.form).type === 'Melee'))
        b += .18;
    if (d.id === 'U05A' && u.hp / d.hp <= .5)
        b += .2;
    if (d.id === 'U05C' && u.hp / d.hp < .3 && !u.flags.lastStand) {
        u.flags.lastStand = s.elapsed + 5;
    }
    if (d.id === 'U05C' && u.flags.lastStand > s.elapsed)
        b += .4;
    if (d.type === 'Melee' && allies.some(a => a.id !== u.id && a.form === 'U06A' && distance(a, u) <= 4))
        b += .08;
    if (d.id === 'U07A' && distance(u, o) >= 10)
        b += .12;
    if (d.id === 'U08C' && u.attacks % 7 === 0) {
        o.flags.scoutUntil = s.elapsed + 3;
        o.flags.scoutValue = .08;
    }
    if (d.id === 'U09A' && u.attacks % 6 === 0)
        extra += 15;
    if (d.id === 'U09B' && s.elapsed - (u.flags.lastDamage ?? -99) >= 4)
        b += .2;
    if (d.id === 'U09C' && u.attacks % 5 === 0)
        o.flags.cracked = s.elapsed + 4;
    if (d.id === 'U10B' && u.flags.targetAttacks <= 3)
        extra += 5;
    if (d.id === 'U12A' && s.elapsed - (u.flags.arrivedAt || s.elapsed) >= 2)
        b += .2;
    if (d.id === 'U12C' && s.elapsed - (u.flags.battery || -11) >= 10 && s.telegraphs.some(t => t.source === o.id) && s.elapsed - (o.flags.delayed || -5) >= 4) {
        s.telegraphs = s.telegraphs.filter(t => t.source !== o.id);
        extra += 25;
        u.flags.battery = s.elapsed;
        o.flags.delayed = s.elapsed;
    }
    if (d.id === 'U13B' && u.attacks % 7 === 0) {
        const low = [...allies].sort((a, b) => a.hp / unitDef(a.form).hp - b.hp / unitDef(b.form).hp)[0];
        if (low)
            shield(s, low, 12, 8, 'rune');
    }
    if (d.id === 'U14A' || d.id === 'U14C') {
        o.flags.burnUntil = s.elapsed + (d.stage === 0 ? 3 : 2);
        o.flags.burn = d.stage === 0 ? 2 : 4;
    }
    if (d.id === 'U14B' && u.flags.targetAttacks % 3 === 0)
        extra += 8;
    if (d.id === 'U15A' && u.attacks % 4 === 0)
        delayHit(s, o, .25);
    if (d.id === 'U15C' && u.attacks % 10 === 0 && s.elapsed - (o.flags.delayed || -5) >= 4) {
        delayHit(s, o, 1);
        if (o.group !== 'boss')
            o.flags.skipHit = 1;
    }
    if (d.id === 'U16B') {
        const key = 'static-' + o.id;
        if (!u.seen.includes(key)) {
            u.seen.push(key);
            u.flags.staticCount = (u.flags.staticCount || 0) + 1;
            if (u.flags.staticCount >= 3) {
                u.buff = .2;
                u.buffUntil = s.elapsed + 4;
                u.flags.staticCount = 0;
                u.seen = u.seen.filter(k => !k.startsWith('static-'));
            }
        }
    }
    if (d.id === 'U17A' && u.flags.targetAttacks % 4 === 0) {
        o.flags.hexUntil = s.elapsed + 4;
        o.flags.hexValue = .06;
    }
    if (d.id === 'U17B' && u.attacks % 6 === 0)
        o.flags.frailty = 2;
    if (d.id === 'U17C' && o.hp / o.max <= .25)
        b += .3;
    if (d.id === 'U18C' && u.attacks % 12 === 0)
        [...allies].sort((a, b) => unitDef(b.form).attack - unitDef(a.form).attack).slice(0, 3).forEach(a => a.flags.prophecy = 6);
    extra += u.flags.prophecy || 0;
    u.flags.prophecy = 0;
    hitEnemy(s, o, d.attack + extra, d.type, b);
    if (d.id === 'U13C' && u.attacks % 6 === 0) {
        o.flags.echo = (o.flags.echo || 0) + d.attack * .5;
        o.flags.echoAt = s.elapsed + .5;
    }
    const splash: Record<string, [
        number,
        number,
        number,
        number
    ]> = { U01C: [6, 10, 2, 2], U07B: [5, 7, 3, 1], U12B: [5, 8, 2, 99], U13A: [5, 5, 2, 2], U16A: [6, 4, 100, 2], U16C: [8, 6, 100, 99], U11A: [8, 8, 100, 1], U11C: [6, 10, 100, 2] };
    const sp = splash[d.id];
    if (sp && u.attacks % sp[0] === 0)
        visibleObjectives(s).filter(e => !e.work && e.id !== o.id && distance(e, o) <= sp[2] && (d.id !== 'U07B' || (e.x - o.x) * (o.x - u.x) + (e.y - o.y) * (o.y - u.y) >= 0)).sort((a, b) => distance(b, u) - distance(a, u)).slice(0, sp[3]).forEach(e => hitEnemy(s, e, sp[1], d.type, b));
    if (d.id === 'U11B' && u.attacks % 7 === 0) {
        const high = visibleObjectives(s).filter(e => !e.work).sort((a, b) => b.hp - a.hp)[0];
        if (high)
            high.flags.hawkUntil = s.elapsed + 5;
    }
    if (o.def === 'E10' && o.flags.reflect > s.elapsed)
        damage(s, u, (d.attack + extra) * .1, o.type, false);
}
function spawnMechanic(s: Run) { s.stage = 1; s.mechanicStart = s.elapsed; const m = mechanics[s.floor - 1]; for (let i = 0; i < m[1] * 2; i++) {
    const lane = Math.floor(i / m[1]);
    const o = makeObjective(s, 'N', 'mechanic', i % m[1], m[1]);
    o.flags.lane = lane;
    o.y = lane === 0 ? 22 : 31;
    o.name += lane === 0 ? ' · North' : ' · South';
    o.spawn = s.floorElapsed;
    s.objectives.push(o);
} if ([3, 6, 8, 9, 11, 12].includes(s.floor))
    spawnBoss(s); story(s, 'major'); }
function spawnBoss(s: Run) { if (s.objectives.some(o => o.group === 'boss'))
    return; let def = `B${String(s.floor).padStart(2, '0')}`; if (s.floor === 12 && s.route > 1)
    def = 'B12X'; const n = s.floor === 7 ? 2 : 1; for (let i = 0; i < n; i++) {
    const o = makeObjective(s, def, 'boss', i, n, s.floorElapsed);
    // Later guardians must withstand an evolved army long enough for preparation to matter.
    const healthScale = 2 * (1 + Math.max(0, s.floor - 3) * .12);
    o.hp *= healthScale; o.max *= healthScale; o.seats *= 2; o.targetSize *= 2; o.reserves *= 2;
    if (s.floor === 12)
        o.spawn = Math.max(14, s.floorElapsed);
    if (s.floor === 2 && s.route > 1) {
        const last = s.objectives.filter(o => o.group === 'mechanic').sort((a, b) => (b.flags.completed || 0) - (a.flags.completed || 0))[0];
        o.type = last?.flags.index === 1 ? 'Magic' : 'Melee';
    }
    if (s.floor === 10 && s.route > 1) {
        const last = s.objectives.filter(o => o.group === 'mechanic').sort((a, b) => (b.flags.completed || 0) - (a.flags.completed || 0))[0];
        o.type = last?.type || 'Melee';
        o.flags.initialType = types.indexOf(o.type);
    }
    s.objectives.push(o);
} if (s.floor === 12 && s.route > 1)
    scene(s, `intro${s.route}`); if (s.floor === 13)
    scene(s, 'self0'); }
function complete(s: Run) {
    for (const o of visibleObjectives(s)) {
        if (o.hp > 0)
            continue;
        if (s.floor === 6 && o.group === 'mechanic' && !o.flags.fired) {
            o.flags.loaded = 1;
            o.hp = 0;
            if (s.route === 2 && !s.rewarded.includes('ballista-captain')) {
                s.rewarded.push('ballista-captain');
                const c = makeObjective(s, 'E07', 'opening', 0, 1, s.floorElapsed);
                c.x = 72;
                c.y = 18;
                s.objectives.push(c);
            }
            continue;
        }
        if (o.group === 'boss' && s.stage < 2) {
            o.hp = 1;
            continue;
        }
        if (o.def === 'B09' && s.window <= 0) {
            o.hp = 1;
            continue;
        }
        o.hp = 0;
        o.done = true;
        o.flags.completed = s.elapsed;
        const draftsBefore = s.queue.length, xpBefore = s.xp;
        if (o.group !== 'side' && !(o.group === 'opening' && s.stage > 0)) {
            const share = o.group === 'opening' ? .2 : o.group === 'mechanic' ? .3 : .5;
            reward(s, `Objective ${o.id}`, share / s.objectives.filter(a => a.group === o.group).length);
        }
        const finalBoss = o.group === 'boss' && s.floor === (s.route === 3 ? 13 : 12);
        s.rewardVfx.push({ id: `objective-${s.floor}-${o.id}`, kind: 'objective', x: o.x, y: o.y, drafts: finalBoss ? 0 : s.queue.length - draftsBefore, xp: s.xp - xpBefore });
        s.rewardVfx = s.rewardVfx.slice(-32);
        float(s, o, 'CLEARED', '#e8cd89');
        s.telegraphs = s.telegraphs.filter(t => t.source !== o.id);
        if (o.group === 'side' && (s.floor !== 3 || s.fog > 0))
            collect(s);
        if (o.group === 'mechanic') {
            if (s.floor === 3 && s.route === 3) {
                o.flags.corridorUntil = s.elapsed + 5;
            }
            if (s.floor === 6) {
                o.flags.shots = (o.flags.shots || 0) + 1;
                o.flags.loaded = 0;
            }
            if (s.floor === 8 && s.route === 2) {
                const partner = s.objectives.find(t => t.group === 'mechanic' && t.flags.index === (o.flags.index ^ 1) && t.flags.lane === o.flags.lane && !t.done);
                if (partner)
                    partner.hp -= 30;
            }
            if (s.floor === 11 && s.route === 3 && s.objectives.some(a => a.id !== o.id && a.group === 'mechanic' && a.done && Math.abs(a.flags.index - o.flags.index) === 1 && s.elapsed - a.flags.completed <= 3)) {
                s.objectives.filter(a => a.group === 'boss').forEach(a => a.flags.patternPause = s.elapsed + 6);
                s.telegraphs = [];
            }
            if (s.floor === 11 || s.floor === 12) {
                s.telegraphs = s.telegraphs.filter(t => t.shape !== anchorPattern(s, o.flags.index));
                if (s.route === 3)
                    s.objectives.filter(t => t.group === 'boss').forEach(t => t.flags.freezeUntil = s.elapsed + 4);
            }
        }
        if (!o.work && o.flags.burn === 4 && o.flags.burnUntil > s.elapsed)
            visibleObjectives(s).filter(a => a.id !== o.id && !a.work && distance(a, o) <= 3).slice(0, 2).forEach(a => { a.flags.burn = 4; a.flags.burnUntil = s.elapsed + 2; });
        if (!o.work)
            s.units.filter(u => u.form === 'U10C' && u.status === 'active').forEach(u => { u.buff = .3; u.buffUntil = s.elapsed + 4; });
    }
    const done = (g: string) => s.objectives.some(o => o.group === g) && s.objectives.filter(o => o.group === g).every(o => o.done);
    if (s.stage === 0 && done('opening')) {

        spawnMechanic(s);
    }
    if (s.stage === 1 && done('mechanic')) {
        s.stage = 2;
        s.fog = 0;

        spawnBoss(s);
        const duration = s.elapsed - s.mechanicStart;
        if ((s.floor === 4 && duration <= 36) || ([5, 8].includes(s.floor) && duration <= 18) || (s.floor === 6 && duration <= 12) || (s.floor === 10 && duration <= 15))
            collect(s);
        if (s.floor === 9) {
            const ms = s.objectives.filter(o => o.group === 'mechanic');
            if (ms.length >= 2 && ms[0].contributors.some(id => !ms[1].contributors.includes(id)))
                collect(s);
            s.window = 10;
        }
        if (s.floor === 11) {
            const first = s.objectives.filter(o => o.group === 'mechanic').sort((a, b) => (a.flags.completed || 0) - (b.flags.completed || 0))[0];
            if (first.flags.index === 3)
                collect(s);
        }
    }
    if (s.stage === 2 && done('boss') && !s.cleared) {
        s.cleared = true;

        s.fog = 0;
        s.telegraphs = [];
        s.floorTimes.push(s.floorElapsed);
        if (!s.units.some(u => unitDef(u.form).type === 'Neutral'))
            achieve(s, 'ACH_NO_WORKER_CLEAR');
        if (s.floor === 7) {
            const bs = s.objectives.filter(o => o.group === 'boss');
            if (Math.abs(bs[0].flags.completed - bs[1].flags.completed) <= 10)
                collect(s);
        }
        if (s.floor === 12 && types.every(t => s.units.some(u => u.status === 'active' && unitDef(u.form).type === t)))
            collect(s);
        if (s.floor === 13 && ['U01', 'U07', 'U13'].every(l => s.units.some(u => unitDef(u.form).line === l)))
            collect(s);
        const terminal = s.floor === (s.route === 3 ? 13 : 12);
        const boss = s.objectives.find(o => o.group === 'boss')!;
        s.rewardVfx.push({ id: `quest-${s.floor}`, kind: 'quest', x: boss.x, y: boss.y, drafts: terminal ? 0 : 2, xp: 0 });
        if (terminal) {
            if (s.route < 3)
                s.collectibles.push(s.route === 1 ? 'COL_CROWN_SHARD_I' : 'COL_CROWN_SHARD_II');
            s.memory += s.queue.length * 25 + (s.floor === 13 ? 50 : 0);
            s.queue = [];
            s.phase = s.route === 3 ? 'ending' : 'victory';
            achieve(s, `ACH_LOOP_${['I', 'II', 'III'][s.route - 1]}_CLEAR`);
            if (s.route < 3) dialogue.filter(d => d.route === s.route && d.floor === 13).forEach(d => { if (!s.seenLines.includes(d.id)) s.seenLines.push(d.id); });
            s.story = [];
            s.storyIndex = 0;
            if (s.route === 3)
                scene(s, 'self3');
            story(s, 'clear');
            story(s, 'end');
            if (s.route === 1)
                scene(s, 'closure');
        }
        else {
            for (let i = 0; i < 2; i++) s.queue.push({ source: 'Floor reinforcements · +2 tiers', tier: Math.min(20, s.tier + 2), cards: [] });

        }
    }
}
export function nextFloor(s: Run) { if (s.phase !== 'transition')
    return; s.carry = Math.min(s.floor >= 3 ? 10 : 20, Math.max(0, s.time)); if (s.floor === 4 || s.floor === 8)
    { if (s.held.length < 6) s.held.push('FX-T01'); else s.memory += 10; s.rerolls = s.held.filter(id => id === 'FX-T01').length; } s.floor++; s.time = Math.round(countdowns[s.floor - 1] * (s.floor <= 3 ? 1.35 : 1)) + s.carry + (!s.hard && s.floor === 2 ? 10 : 0); s.phase = 'playing'; s.effects.filter(f => f.delay > 0 && effectDef(f.card).kind === 'Kept local').forEach(f => { if (s.held.length < 6)
    s.held.push(f.card);
else
    s.memory += 10; }); s.effects = s.effects.filter(f => effectDef(f.card).kind === 'Kept global' && effectDef(f.card).timing !== 'Floor-long'); s.units.forEach(u => { const d = unitDef(u.form); u.hp = Math.min(d.hp, u.hp + d.hp * (s.passives.includes('FX-P04') ? .3 : .25)); u.status = u.hp >= d.hp * .6 || alive(u) ? 'unassigned' : 'KO'; u.x = 47 + random(s) * 6; u.y = 39; u.target = null; u.attacks = 0; u.flags = {}; u.seen = []; u.clock = 0; u.koTime = 2; }); loadFloor(s); }
function areaContains(t: Telegraph, u: Unit) { const dx = u.x - t.x, dy = u.y - t.y; const r = Math.hypot(dx, dy); if (t.shape === 'pulse' || t.shape === 'gust')
    return r <= 20; if (t.shape === 'ring')
    return r >= 5 && r <= 12; if (t.shape === 'shelter')
    return distance(u, { x: t.safeX ?? 50, y: t.safeY ?? 27 }) > 6; const angle = Math.atan2(dy, dx) - t.angle; const forward = Math.cos(angle) * r, side = Math.sin(angle) * r; if (t.shape === 'cone')
    return r <= 14 && forward > 0 && Math.abs(Math.atan2(side, forward)) <= 35 * Math.PI / 180; return Math.abs(side) <= 1.5 && Math.abs(forward) <= 30 || (t.shape === 'lanes' && Math.abs(side - 7) <= 1.5 && Math.abs(forward) <= 30); }
export function anchorPattern(s: Run, index: number) { return (s.floor === 12 ? (s.route === 1 ? ['cone', 'line', 'ring', 'pulse'] : ['lanes', 'ring', 'cone', 'pulse']) : ['line', 'cone', 'ring', 'pulse'])[index]; }
function queueArea(s: Run, o: Objective, shape: string, amount: number) {
    if (s.floor >= 11 && s.floor <= 12 && s.objectives.some(a => a.group === 'mechanic' && a.done && anchorPattern(s, a.flags.index) === shape))
        return;
    if (o.def === 'B09' && s.route === 3 && shape === 'line' && s.objectives.some(a => a.group === 'mechanic' && a.flags.index === 0 && a.done))
        return;
    const party = active(s, o);
    const target = party[o.cursor % Math.max(1, party.length)];
    let angle = target ? Math.atan2(target.y - o.y, target.x - o.x) : Math.PI / 2;
    if (o.def === 'B01' && s.route === 2) {
        angle = o.flags.coneAngle ?? angle;
        o.flags.coneAngle = angle + Math.PI / 2;
    }
    if (o.def === 'B05' && s.route === 2) {
        const cages = s.objectives.filter(a => a.group === 'mechanic').sort((a, b) => (b.flags.completed || 0) - (a.flags.completed || 0));
        const cage = cages[((o.flags.blastCount || 1) - 1) % 3];
        if (cage)
            angle = Math.atan2(cage.y - o.y, cage.x - o.x);
    }
    const t: Telegraph = { id: uid(s, 'area'), source: o.id, x: o.x, y: o.y, shape, remaining: 2, damage: amount, type: o.type, angle };
    if (shape === 'shelter') {
        const lamps = s.objectives.filter(a => a.group === 'mechanic');
        const lamp = s.route === 3 ? [...lamps].sort((a, b) => (a.flags.shelterUses || 0) - (b.flags.shelterUses || 0) || a.flags.index - b.flags.index)[0] : lamps[Math.floor(s.elapsed / 12) % Math.max(1, lamps.length)];
        t.safeX = lamp?.x || 50;
        t.safeY = lamp?.y || 27;
        if (lamp)
            lamp.flags.shelterUses = (lamp.flags.shelterUses || 0) + 1;
    }
    if (o.def === 'B07' && s.route === 3) {
        const priorities = ['Important', 'Normal', 'Ignore'];
        o.flags.copiedPriority = priorities.indexOf(o.priority);
    }
    s.telegraphs.push(t);
}
export function takeShelter(s: Run, id: string) { const t = s.telegraphs.find(t => t.source === id && t.shape === 'shelter'); if (!t)
    return; s.units.filter(u => u.target === id && alive(u) && u.status !== 'reserve').forEach((u, i) => { u.ax = (t.safeX || 50) + Math.cos(i * 2.4) * 2; u.ay = (t.safeY || 27) + Math.sin(i * 2.4) * 2; u.status = 'traveling'; u.arrival = 0; }); }
export function reformParty(s: Run, id: string) { s.units.filter(u => u.target === id && alive(u)).forEach(u => { u.target = null; u.status = 'unassigned'; }); }
function updateBoss(s: Run, o: Objective, e: EnemyDef, dt: number) {
    let phase = o.hp / o.max <= .33 ? 2 : o.hp / o.max <= .66 ? 1 : 0;
    if (phase !== o.phase) {
        o.phase = phase;
        if (o.def === 'B13')
            scene(s, `self${phase}`);
        // Damage phases must not erase a warned attack or restart its cooldown.
        if (['B10', 'B12'].includes(o.def))
            o.type = o.def === 'B12' ? ['Magic', 'Range', 'Melee'][phase] as CombatType : types[((o.flags.initialType || 0) + phase) % 3];
        if (o.def === 'B13' && phase > 0) {
            const party = active(s, o);
            const counts = types.map(t => party.filter(u => unitDef(u.form).type === t).length);
            const max = Math.max(...counts);
            if (max > 0) {
                const prior = types.indexOf(o.type);
                o.type = [1, 2, 3].map(i => types[(prior + i) % 3]).find(t => counts[types.indexOf(t)] === max)!;
            }
        }
    }
    if (o.def === 'E10') {
        const q = Math.floor((1 - Math.max(0, o.hp / o.max)) * 4);
        if (q > (o.flags.quarter || 0)) {
            o.flags.quarter = q;
            o.flags.reflect = s.elapsed + 6;
        }
    }
    if (o.def === 'B12X' && !(o.flags.freezeUntil > s.elapsed)) {
        o.flags.rotationTime = (o.flags.rotationTime || 0) + dt;
        o.type = types[Math.floor(o.flags.rotationTime / 12) % 3];
    }
    if (o.def === 'E09' && o.hp <= o.max * .5 && !o.flags.summoned && visibleObjectives(s).filter(a => a.def === 'E01').length < 4) {
        o.flags.summoned = 1;
        const imp = makeObjective(s, 'E01', 'opening', 0, 1, s.floorElapsed);
        imp.x = o.x + 6;
        imp.y = o.y;
        s.objectives.push(imp);
    }
    if (o.def === 'E01' && o.hp <= o.max * .2 && !o.flags.retreated) {
        o.flags.retreated = 1;
        o.flags.retreatUntil = s.elapsed + 2;
        o.flags.originX = o.x;
        o.x = clamp(o.x + (s.route === 2 ? (o.x < 50 ? -8 : 8) : 5), 1, 99);
    }
    if (o.def === 'E01' && o.flags.retreatUntil && s.elapsed >= o.flags.retreatUntil) {
        o.x = o.flags.originX;
        o.flags.retreatUntil = 0;
        if (s.route === 2)
            o.flags.pursuitUntil = s.elapsed + 4;
    }
    if (o.def === 'E12' && o.engaged && (o.flags.healClock = (o.flags.healClock || 0) + dt) >= 5) {
        o.flags.healClock = 0;
        const ally = visibleObjectives(s).find(a => a.id !== o.id && !a.work);
        if (ally)
            ally.hp = Math.min(ally.max, ally.hp + 25);
    }
    if (o.flags.echoAt && o.flags.echoAt <= s.elapsed) {
        hitEnemy(s, o, o.flags.echo || 0, 'Magic');
        o.flags.echo = 0;
        o.flags.echoAt = 0;
    }
    if ((e.aoe || o.group === 'boss') && o.engaged) {
        if (o.def === 'B11') {
            ['line', 'cone', 'ring', 'pulse'].forEach((shape, i) => {
                const disabled = s.objectives.some(a => a.group === 'mechanic' && a.done && a.flags.index === i);
                if (disabled || o.flags.patternPause > s.elapsed)
                    return;
                const key = 'pattern-' + i;
                o.flags[key] = (o.flags[key] || 0) + dt;
                const cleared = s.objectives.filter(a => a.group === 'mechanic' && a.done).length;
                const cadence = (4 + i / 3) * (s.route === 2 ? Math.max(.75, 1 - cleared * .05) : 1);
                if (o.flags[key] >= cadence) {
                    o.flags[key] = 0;
                    queueArea(s, o, shape, 22);
                }
            });
        }
        else {
            o.areaClock += dt;
            let cooldown = e.aoe || 4;
            if (o.def === 'B12' || o.def === 'B12X' || o.def === 'B13')
                cooldown = (o.def === 'B12X' ? [8, 7, 6] : [10, 8, 7])[phase];
            if (o.group === 'boss') cooldown = 4;
            if (o.areaClock >= cooldown) {
                o.areaClock = 0;
                let shape = e.shape || 'pulse';
                let amount = e.area || 12;
                if (s.floor <= 3) { shape = 'pulse'; amount *= 1.5; }
                if (o.def === 'B12') {
                    shape = ['cone', 'line', 'ring'][phase];
                    amount = [18, 22, 24][phase];
                }
                if (o.def === 'B12X') {
                    shape = ['lanes', 'ring', 'cone'][phase];
                    amount = [20, 22, 24][phase];
                }
                if (o.def === 'B13') {
                    shape = { Melee: 'cone', Range: 'lanes', Magic: 'ring', Neutral: 'pulse' }[o.type];
                    amount = [18, 20, 24][phase];
                }
                if (o.def === 'B05') {
                    o.flags.blastCount = (o.flags.blastCount || 0) + 1;
                    if (s.route === 1 && o.flags.blastCount % 2 === 0)
                        shape = 'shelter';
                }
                queueArea(s, o, shape, amount);
                if (o.def === 'B09' && s.route === 3 && !s.objectives.some(a => a.group === 'mechanic' && a.flags.index === 1 && a.done))
                    queueArea(s, o, 'gust', 0);
            }
            if (o.def.startsWith('B12') && phase === 2) {
                o.flags.pulseClock = (o.flags.pulseClock || 0) + dt;
                if (o.flags.pulseClock >= (s.route === 1 ? 5 : 4)) {
                    o.flags.pulseClock = 0;
                    queueArea(s, o, 'pulse', s.route === 1 ? 12 : 14);
                }
            }
        }
    }
}
// Base participation hit, before type matchups, shields and encounter debuffs.
export function encounterHit(s: Run, o: Objective) {
    if (o.work) return Math.min(6, 1 + s.floor);
    const e = enemyDef(o.def);
    if (!e) return Math.min(6, 1 + s.floor);
    const hit = o.def.startsWith('B12') || o.def === 'B13'
        ? (o.def === 'B13' ? [36, 40, 42] : [38, 40, 42])[o.phase]
        : e.hit;
    return hit * (s.hard ? 1.15 : 1) * .65;
}

export function tick(s: Run, dt = .1, autoDialogue = true) {
    if (paused(s)) {
        ensureDraft(s);
        return;
    }
    for (const command of s.commands.splice(0)) {
        if (command.kind === 'priority')
            applyPriority(s, command.id!, command.priority!);
        else if (command.kind === 'camp')
            applyCamp(s, command.id!);
        else
            applyWake(s, command.id);
    }
    s.elapsed += dt;
    s.floorElapsed += dt;
    if (s.floor === 1 && s.floorElapsed >= 5 && !s.rewarded.includes('arrival-preparation')) {
        s.rewarded.push('arrival-preparation');
        s.queue.push({ source: 'Prepare for the valley', tier: 1, cards: [] });
    }
    s.attackVfx = s.attackVfx.filter(f => (f.life -= dt) > 0);
    s.vfx.forEach(f => f.life -= dt);
    s.vfx = s.vfx.filter(f => f.life > 0);
    s.storyClock = s.storyIndex < s.story.length ? s.storyClock + dt : 0;
    if (autoDialogue && s.storyClock >= 5)
        advanceStory(s);
    s.window = Math.max(0, s.window - dt);
    s.floats = s.floats.filter(f => (f.life -= dt) > 0);
    assign(s);
    for (const u of s.units) {
        const d = unitDef(u.form);
        u.shields = u.shields.filter(f => f.until > s.elapsed);
        u.shield = u.shields.reduce((n, f) => n + f.amount, 0);
        if (u.status === 'retreating') {
            u.koTime += dt;
            move(u, 2 * dt);
            if (u.koTime >= 2) {
                u.x = u.ax;
                u.y = u.ay;
                u.status = 'KO';
            }
            continue;
        }
        if (u.status === 'KO') {
            u.hp = Math.min(d.hp * .6, u.hp + d.hp * .05 * dt);
            if (u.hp >= d.hp * .6 - .001) {
                u.hp = d.hp * .6;
                u.status = 'unassigned';
                s.recovered++;
                if (s.recovered >= 3)
                    achieve(s, 'ACH_RECOVERY_PLAN');
            }
            continue;
        }
        if (u.status === 'unassigned' && u.hp >= d.hp)
            u.status = 'wandering';
        if (u.status === 'wandering') {
            const camp = campPosition(s, u);
            if (u.hp < d.hp) {
                u.status = 'camp-bound';
                u.ax = camp.x;
                u.ay = camp.y;
            } else {
                // Idle motion is deterministic and does not consume combat RNG.
                const phase = s.elapsed * .3 + Number(u.id.split('-')[1]) * 2.399;
                u.ax = camp.x + Math.cos(phase) * 2;
                u.ay = camp.y - 2.5 + Math.sin(phase) * .7;
                move(u, d.speed * .2 * dt);
            }
        }
        if (['traveling', 'reserve', 'camp-bound', 'unassigned'].includes(u.status)) {
            if (u.status === 'unassigned') {
                const camp = campPosition(s, u);
                u.ax = camp.x;
                u.ay = camp.y;
            }
            const speed = d.speed * (d.id === 'U08A' && u.status === 'reserve' ? 1.25 : 1) * (s.passives.includes('FX-P02') && s.objectives.find(o => o.id === u.target)?.work ? 1.1 : 1);
            if (move(u, speed * dt)) {
                if (u.status === 'traveling') {
                    u.arrival += dt;
                    if (u.arrival >= .5) {
                        u.status = 'ready';
                        u.flags.arrivedAt = s.elapsed;
                    }
                }
                if (['unassigned', 'camp-bound'].includes(u.status))
                    u.status = 'camping';
            }
        }
        if (u.status === 'camping') {
            heal(s, u, d.hp * (s.passives.includes('FX-P01') ? .025 : .02) * dt);
            if (u.hp >= d.hp) u.status = 'wandering';
        }
    }
    for (const o of visibleObjectives(s)) {
        const party = active(s, o);
        const traveling = s.units.some(u => u.target === o.id && u.status === 'traveling');
        if (o.work) {
            party.forEach(u => u.status = 'active');
            o.engaged = party.length > 0;
        }
        else {
            if (o.strategy === 'Careful' && o.engaged && party.length < Math.ceil(o.targetSize / 2)) {
                o.engaged = false;
                o.targetSize = o.seats;
                o.fallback = 0;
                party.forEach(u => u.status = 'ready');
            }
            if (!o.engaged && party.length) {
                if (o.strategy === 'Rush' || party.length >= o.seats) {
                    o.engaged = true;
                    o.clock = 0;
                }
                else if (!traveling) {
                    o.fallback += dt;
                    if (o.fallback >= 5) {
                        o.engaged = true;
                        o.targetSize = party.length;
                        o.clock = 0;
                    }
                }
                else
                    o.fallback = 0;
            }
            if (o.engaged)
                party.forEach(u => u.status = 'active');
        }
        for (const u of party.filter(u => u.status === 'active')) {
            const key = 'arrival-' + o.id;
            if (!u.seen.includes(key)) {
                u.seen.push(key);
                if (u.form === 'U04A' && !o.work)
                    hitEnemy(s, o, 12, 'Melee');
                if (u.form === 'U08B' && !o.work)
                    party.filter(a => a.status === 'active').forEach(a => { a.buff = .1; a.buffUntil = s.elapsed + 4; });
            }
        }
        const e = enemyDef(o.def);
        if (e)
            updateBoss(s, o, e, dt);
    }
    for (const t of [...s.telegraphs]) {
        t.remaining -= dt;
        if (t.remaining <= 0) {
            const source = s.objectives.find(o => o.id === t.source);
            if (source && !source.done && source.hp > 0) {
                const foresight = active(s, source).find(u => u.form === 'U18B' && !u.seen.includes('blast-' + source.id));
                if (foresight) {
                    t.damage *= .8;
                    foresight.seen.push('blast-' + source.id);
                }
                s.units.filter(u => ['active', 'ready', 'camp-bound'].includes(u.status) && areaContains(t, u) && !(s.floor === 5 && s.route === 3 && s.objectives.some(a => a.group === 'mechanic' && a.done && !a.flags.closed && distance(u, a) <= 6))).forEach(u => { damage(s, u, t.damage, t.type, false); if (t.shape === 'gust' && alive(u)) {
                    const r = Math.max(1, distance(u, source));
                    u.x = clamp(u.x + (u.x - source.x) / r * 4, .5, 99.5);
                    u.y = clamp(u.y + (u.y - source.y) / r * 4, .5, 42.35);
                    u.status = 'traveling';
                    u.arrival = 0;
                } });
                if (s.floor === 5 && s.route === 3 && t.shape === 'cone') {
                    const shelter = s.objectives.filter(a => a.group === 'mechanic' && a.done && !a.flags.closed).sort((a, b) => s.units.filter(u => alive(u) && distance(u, b) <= 6).length - s.units.filter(u => alive(u) && distance(u, a) <= 6).length)[0];
                    if (shelter)
                        shelter.flags.closed = 1;
                }
            }
            s.telegraphs = s.telegraphs.filter(a => a.id !== t.id);
        }
    }
    for (const o of visibleObjectives(s)) {
        const e = enemyDef(o.def);
        if (!e && o.engaged && o.hp > 0) {
            o.clock += dt;
            if (o.clock >= 1) {
                o.clock -= 1;
                active(s, o).forEach(u => damage(s, u, encounterHit(s, o), o.type));
            }
        }
        if (!e || o.hp <= 0 || !o.engaged)
            continue;
        const party = active(s, o).filter(u => u.status === 'active').sort((a, b) => (a.flags.arrivedAt || 0) - (b.flags.arrivedAt || 0) || a.id.localeCompare(b.id));
        if (!party.length)
            continue;
        o.clock += dt;
        const frost = inEffect(s, o, ['FX-L05']).length ? .5 : 0;
        const rage = o.def === 'B07' && s.objectives.some(a => a.def === 'B07' && a.done && s.elapsed - a.flags.completed > 10) ? .8 : 1;
        const interval = (1 + frost) * rage;
        if (o.clock >= interval) {
            o.clock -= interval;
            const target = party[o.cursor++ % party.length];
            if (o.flags.skipHit) {
                o.flags.skipHit = 0;
                continue;
            }
            let hit = encounterHit(s, o);
            if (o.flags.frailty > 0) {
                hit *= .88;
                o.flags.frailty--;
            }
            if (o.def === 'E03') {
                if (o.flags.marked === Number(target.id.split('-')[1]))
                    hit += 4 * .65;
                o.flags.marked = Number(target.id.split('-')[1]);
            }
            party.forEach(u => damage(s, u, hit, o.type));
        }
    }
    for (const u of s.units) {
        if (u.status !== 'active' || !alive(u))
            continue;
        const o = s.objectives.find(a => a.id === u.target && !a.done);
        if (!o || o.hp <= 0 || !o.engaged)
            continue;
        const range = o.work || unitDef(u.form).type === 'Neutral' ? 3 : unitDef(u.form).type === 'Melee' ? 2.5 : unitDef(u.form).type === 'Range' ? 18 : 14;
        if (distance(u, o) > range)
            continue;
        u.clock += dt;
        u.flags.attackClock = (u.flags.attackClock || 0) + dt;
        if (u.flags.attackClock >= 1) {
            u.flags.attackClock -= 1;
            attack(s, u, o);
        }
    }
    complete(s);
    for (const o of visibleObjectives(s)) {
        if (o.flags.burnUntil > s.elapsed)
            hitEnemy(s, o, o.flags.burn * dt, 'Magic', 0);
    }
    if (s.floor === 3 && s.stage === 1) {
        const done = s.objectives.filter(o => o.group === 'mechanic' && o.done).length;
        const t = s.elapsed - s.mechanicStart;
        const step = t < 10 ? 0 : Math.max(0, Math.min(4, 1 + Math.floor((t - 10) / 6)) - done);
        s.fog = [0, .5, 1, 1.5, 2][step];
        if (!(s.route === 2 && Math.floor(t / 8) % 2 === 1))
            s.units.filter(u => !['KO', 'retreating', 'camping'].includes(u.status) && !(s.route === 3 && s.objectives.some(o => o.flags.corridorUntil > s.elapsed && distance(u, { x: o.x + (s.elapsed - o.flags.completed) * 3, y: o.y }) <= 6))).forEach(u => damage(s, u, s.fog * dt, 'Neutral', false, true));
    }
    if (s.floor === 9 && s.stage === 2 && s.window <= 0) {
        const last = s.objectives.find(o => o.group === 'boss');
        if (last) {
            last.flags.land = (last.flags.land || 0) + dt;
            if (last.flags.land >= (s.route === 2 ? 5 : 4)) {
                s.window = 10;
                last.flags.land = 0;
                if (s.route === 2) {
                    const beacon = s.objectives.find(a => a.group === 'mechanic' && a.flags.index === (last.flags.scorchIndex || 0));
                    if (beacon)
                        beacon.flags.scorchedUntil = s.elapsed + 5;
                    last.flags.scorchIndex = ((last.flags.scorchIndex || 0) + 1) % 2;
                }
            }
        }
    }
    if (s.floor === 2 && s.route === 3 && s.stage === 1) {
        for (const o of visibleObjectives(s).filter(o => o.group === 'mechanic')) {
            if (o.hp < o.max && s.elapsed - (o.flags.lastWorked || s.mechanicStart) >= 4 && s.elapsed - (o.flags.transfer || s.mechanicStart) >= 4) {
                const partner = visibleObjectives(s).find(a => a.group === 'mechanic' && a.id !== o.id);
                if (partner) {
                    partner.hp -= Math.min(30, o.max - o.hp);
                    o.flags.transfer = s.elapsed;
                    float(s, partner, '+30 WORK');
                }
            }
        }
    }
    if (s.floor === 10 && s.route === 3 && s.stage === 1)
        s.objectives.filter(o => o.group === 'mechanic' && !o.done).forEach(o => o.type = types[(o.flags.index + Math.floor((s.elapsed - s.mechanicStart) / 6)) % 3]);
    if (s.floor === 6 && s.stage >= 2 && s.window <= 0) {
        const ballista = s.objectives.find(o => o.group === 'mechanic' && o.done);
        if (ballista) {
            ballista.done = false;
            ballista.hp = ballista.max;
            ballista.flags.reloading = 1;
            ballista.flags.fired = 0;
            ballista.flags.loaded = 0;
        }
    }
    for (const f of s.effects) {
        if (f.delay > 0) {
            f.delay -= dt;
            if (f.delay > 0)
                continue;
            if (effectDef(f.card).kind === 'Kept local')
                s.lastLocal = f.card;
            if (effectDef(f.card).timing === 'Immediate') {
                if (f.card === 'FX-L06')
                    s.units.filter(u => alive(u) && u.hp / unitDef(u.form).hp < .25 && distance(u, f) <= 6).forEach(u => { u.x = 50; u.y = 39.5; u.target = null; u.status = 'camping'; });
                if (f.card === 'FX-L10')
                    s.units.filter(u => alive(u) && ['unassigned', 'wandering', 'camping'].includes(u.status)).forEach(u => { u.x = f.x; u.y = f.y; u.status = 'unassigned'; });
            }
        }
        if (f.card === 'FX-L03')
            s.units.filter(u => alive(u) && ['unassigned', 'wandering', 'camping'].includes(u.status) && distance(u, f) <= 8).forEach(u => heal(s, u, unitDef(u.form).hp * .03 * dt));
        f.remaining -= dt;
    }
    s.effects = s.effects.filter(f => f.remaining > 0);
    complete(s);
    if (s.units.some(u => u.status === 'active') && visibleObjectives(s).some(o => types.every(t => active(s, o).some(u => unitDef(u.form).type === t))))
        achieve(s, 'ACH_TRIAD');
    s.time = Math.max(0, s.time - dt);
    if (s.time <= 0 && !s.cleared) {
        s.phase = 'defeat';
        s.queue = [];
        note(s, 'The expedition ran out of time.');
    }
    ensureDraft(s);
    postDraft(s);
}
export function requestBallista(s: Run, id: string) { const o = s.objectives.find(o => o.id === id); if (!o?.flags.loaded)
    return; if (s.route === 3)
    s.shotChoice = id;
else
    fireBallista(s, id, 'heart'); }
export function fireBallista(s: Run, id: string, choice: 'heart' | 'telegraphs') { const o = s.objectives.find(o => o.id === id); if (!o?.flags.loaded || o.done)
    return; o.flags.fired = 1; o.flags.loaded = 0; if (choice === 'heart') {
    s.window = Math.min(24, s.window + 8);
    note(s, 'Ballista fired: +8 seconds of Heart exposure');
}
else {
    s.telegraphs = s.telegraphs.filter(t => !['line', 'lanes'].includes(t.shape));
    note(s, 'Ballista fired: line telegraphs cleared');
} s.shotChoice = null; float(s, o, choice === 'heart' ? 'HEART EXPOSED' : 'LINES CLEARED'); complete(s); ensureDraft(s); }
export function fieldStatus(s: Run) {
    const boss = visibleObjectives(s).find(o => o.group === 'boss');
    if (s.floor === 4 && s.stage === 1) {
        const t = (s.elapsed - s.mechanicStart) % 12;
        const color = Math.floor((s.elapsed - s.mechanicStart) / 12) % 3;
        return t >= 9 ? `NEXT SIGIL: ${['BLUE', 'GREEN', 'RED'][(color + 1) % 3]} · ${Math.ceil(12 - t)}s` : `ACTIVE SIGIL: ${['BLUE', 'GREEN', 'RED'][color]} · ${Math.ceil(12 - t)}s`;
    }
    if (s.floor === 6 && s.stage >= 1)
        return s.window > 0 ? `HEART EXPOSED · ${Math.ceil(s.window)}s` : 'LOAD BALLISTAS → FIRE TO EXPOSE';
    if (s.floor === 9 && s.stage >= 1)
        return s.window > 0 ? `ROC LANDED · ${Math.ceil(s.window)}s` : 'ROC AIRBORNE · 25% DAMAGE';
    if (boss?.def === 'B12X') {
        const t = (boss.flags.rotationTime || 0) % 12;
        return boss.flags.freezeUntil > s.elapsed ? `VULNERABILITY FROZEN · ${boss.type}` : t >= 10 ? `NEXT TYPE: ${types[(types.indexOf(boss.type) + 1) % 3]} · ${Math.ceil(12 - t)}s` : `KING TYPE: ${boss.type} · ${Math.ceil(12 - t)}s`;
    }
    if (s.floor === 3 && s.stage === 1) {
        if (s.route === 2 && Math.floor((s.elapsed - s.mechanicStart) / 8) % 2 === 1)
            return 'VIOLET FOG · WORK ×0.5';
        return s.fog ? `GREEN FOG · ${s.fog} HP/s` : 'FOG GRACE · OPEN THE VENTS';
    }
    if (s.telegraphs.length)
        return `⚠ ${s.telegraphs[0].shape.toUpperCase()} · ${s.telegraphs[0].remaining.toFixed(1)}s`;
    return 'CAMP IS SAFE';
}
